import { ref, watch } from 'vue'

import { logInfra } from '@/utils/logInfra'
import { CHUNK_LOAD_ERROR_INFRA_EVENT, VITE_PRELOAD_ERROR_EVENT } from '@/constants/CHUNK_LOAD'

type VitePreloadErrorEvent = Event & {
  payload?: unknown,
}

// One broken chunk rejects for every component instance that tries to load it,
// so each distinct error is reported once per page load.
const reportedErrorMessages = new Set<string>()

// Chunk-load errors seen on this page so far, shared with useChunkLoadError.
const chunkLoadErrorCount = ref(0)

let isTracking = false

const handlePreloadError = (event: Event) => {
  const { payload } = event as VitePreloadErrorEvent
  const message = payload instanceof Error ? payload.message : String(payload)

  if (!reportedErrorMessages.has(message)) {
    reportedErrorMessages.add(message)
    logInfra(CHUNK_LOAD_ERROR_INFRA_EVENT, { message })
  }

  chunkLoadErrorCount.value += 1
}

// Starts listening for Vite's chunk-load errors (a lazy route or component whose
// file the browser could not fetch, typically during or after a deploy) and
// reports each one to LogRocket. Called from main.js before the router starts
// the first navigation, so a failing initial route chunk is caught even though
// the root component mounts later. The event is not prevented, so the failing
// import still rejects as before.
export const startChunkLoadErrorTracking = () => {
  if (isTracking) {
    return
  }
  isTracking = true
  window.addEventListener(VITE_PRELOAD_ERROR_EVENT, handlePreloadError)
}

// Calls `onChunkLoadError` after chunk-load errors, including ones that happened
// before the calling component was set up, so the caller can offer a reload.
export const useChunkLoadError = (onChunkLoadError: () => void) => {
  watch(chunkLoadErrorCount, (count) => {
    if (count > 0) {
      onChunkLoadError()
    }
  }, { immediate: true })
}
